'use client'

import { MobileLogoutButton } from '@/shared/components/auth/LogoutButtons'
import ResponsiveLogo from '@/shared/components/header/ResponsiveLogo'
import {
  softEditorialEase,
  softEditorialEaseInOut,
  softMotionDuration,
} from '@/shared/components/motion/motion-tokens'
import { ServiceIcon } from '@/shared/components/ServiceIcon'
import {
  aboutNavigationItems,
  serviceNavigationItems,
  treatmentNavigationItems,
} from '@/shared/components/navigation/navigation-data'
import { buildAccountLinks } from '@/shared/components/navigation/account-navigation'
import { useDetectClickOutside } from '@/shared/hooks/useDetectClickOutside'
import { PricingValue } from '@/lib/cms/pricing'
import { PATIENT_ROLE } from '@/lib/constants'
import { user } from '@/lib/utils'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronLeft, faChevronRight } from '@fortawesome/pro-light-svg-icons'
import { AnimatePresence, motion, type Variants } from 'framer-motion'
import type { Session } from 'next-auth'
import Image from 'next/image'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useEffect, useState, type ReactNode } from 'react'

type MenuPanel = 'root' | 'services' | 'treatments' | 'about' | 'account'

const panelSlideVariants: Variants = {
  enter: (slideDirection: number) => ({
    x: slideDirection > 0 ? 8 : -8,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
    transition: {
      delay: 0.08,
      duration: 0.48,
      ease: softEditorialEase,
    },
  },
  exit: (slideDirection: number) => ({
    x: slideDirection > 0 ? -6 : 6,
    opacity: 0,
    transition: {
      duration: 0.3,
      ease: softEditorialEaseInOut,
    },
  }),
}

type Props = {
  session: Session | null
  onLogout: () => Promise<void>
}

export default function MobileNavigationSidebar({ session, onLogout }: Props) {
  const pathname = usePathname()
  const [open, setOpen] = useState(false)
  const [activePanel, setActivePanel] = useState<MenuPanel>('root')
  const [direction, setDirection] = useState(1)
  const panelRef = useDetectClickOutside<HTMLDivElement>({
    onTriggered: closeMenu,
  })

  function closeMenu() {
    setOpen(false)
    setActivePanel('root')
  }

  function openPanel(panel: Exclude<MenuPanel, 'root'>) {
    setDirection(1)
    setActivePanel(panel)
  }

  function returnToMenu() {
    setDirection(-1)
    setActivePanel('root')
  }

  useEffect(() => {
    setOpen(false)
    setActivePanel('root')
  }, [pathname])

  useEffect(() => {
    if (!open) return

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [open])

  const accountLinks = buildAccountLinks(session?.user?.roles ?? [])

  return (
    <>
      <button
        type="button"
        className="flex h-[22px] w-[22px] items-center justify-center rounded-sm text-[#0b0b0f] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#2563eb]"
        aria-label="Open navigation"
        aria-expanded={open}
        aria-controls="mobile-navigation"
        onClick={() => setOpen(true)}
      >
        <svg
          aria-hidden="true"
          width="22"
          height="22"
          viewBox="0 0 22 22"
          fill="none"
        >
          <rect x="2" y="6" width="18" height="2" rx="1" fill="currentColor" />
          <rect x="2" y="13" width="18" height="2" rx="1" fill="currentColor" />
        </svg>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            id="mobile-navigation"
            className="fixed inset-0 z-[999] flex justify-end bg-black/20 p-0 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.48, ease: softEditorialEase }}
          >
            <motion.div
              ref={panelRef}
              className="grid h-dvh w-full max-w-[375px] overflow-hidden rounded-l-[24px] bg-white shadow-2xl"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{
                duration: softMotionDuration.panel,
                ease: softEditorialEaseInOut,
              }}
            >
              <AnimatePresence initial={false} mode="sync" custom={direction}>
                <motion.div
                  key={activePanel}
                  custom={direction}
                  className="col-start-1 row-start-1 h-full w-full overflow-y-auto"
                  variants={panelSlideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                >
                  {activePanel === 'root' && (
                    <RootMenu
                      session={session}
                      onLogout={onLogout}
                      closeMenu={closeMenu}
                      openPanel={openPanel}
                    />
                  )}
                  {activePanel === 'services' && (
                    <ServicesPanel
                      closeMenu={closeMenu}
                      returnToMenu={returnToMenu}
                    />
                  )}
                  {activePanel === 'treatments' && (
                    <TreatmentsPanel
                      closeMenu={closeMenu}
                      returnToMenu={returnToMenu}
                    />
                  )}
                  {activePanel === 'about' && (
                    <AboutPanel
                      closeMenu={closeMenu}
                      returnToMenu={returnToMenu}
                    />
                  )}
                  {activePanel === 'account' && (
                    <AccountPanel
                      links={accountLinks}
                      closeMenu={closeMenu}
                      returnToMenu={returnToMenu}
                    />
                  )}
                </motion.div>
              </AnimatePresence>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

function RootMenu({
  session,
  onLogout,
  closeMenu,
  openPanel,
}: {
  session: Session | null
  onLogout: () => Promise<void>
  closeMenu: () => void
  openPanel: (panel: Exclude<MenuPanel, 'root'>) => void
}) {
  const medicalCertificates = serviceNavigationItems[0]
  const weightLoss = treatmentNavigationItems[0]
  const isPatient = user(session).contains(PATIENT_ROLE)

  return (
    <div className="flex min-h-full flex-col px-5 pb-5 pt-3.5">
      <MobileMenuHeader closeMenu={closeMenu} />

      <div className="grid grid-cols-2 gap-3">
        <Link
          href={medicalCertificates.href}
          onClick={closeMenu}
          className="group flex h-[150px] flex-col justify-between rounded-[22px] bg-[#fcecd2] p-4 transition-colors duration-[360ms] ease-[cubic-bezier(0.22,0.55,0.28,1)] hover:bg-[#f9e2c0] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2563eb] active:bg-[#f9e2c0]"
        >
          <ServiceIcon
            kind={medicalCertificates.icon}
            className="h-9 w-9"
            iconClassName="h-[15px] w-[15px]"
          />
          <span className="flex flex-col gap-0.5">
            <span className="text-[16px] font-bold leading-5 text-[#0b0b0f]">
              Medical certificates
            </span>
            <span className="flex items-center justify-between">
              <span className="text-[13px] font-semibold leading-4 text-[#7a6647]">
                from <PricingValue name={medicalCertificates.priceName} />
              </span>
              <svg
                aria-hidden="true"
                className="h-3.5 w-3.5 text-[#7a6647] opacity-45 transition-[color,opacity] duration-[360ms] ease-[cubic-bezier(0.22,0.55,0.28,1)] group-hover:text-[#0b0b0f] group-hover:opacity-100 group-active:text-[#0b0b0f] group-active:opacity-100"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M5 12h14m-6-6 6 6-6 6"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </span>
        </Link>

        <Link
          href={weightLoss.href}
          onClick={closeMenu}
          className="group relative flex h-[150px] flex-col justify-end overflow-hidden rounded-[22px] p-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2563eb]"
        >
          <Image
            src={weightLoss.image}
            alt=""
            fill
            sizes="162px"
            className="object-cover object-[50%_30%] transition-transform duration-500 ease-[cubic-bezier(0.22,0.55,0.28,1)] group-hover:scale-[1.03] group-active:scale-[1.03]"
          />
          <span className="absolute inset-0 bg-gradient-to-b from-transparent from-35% to-black/60" />
          <span className="absolute inset-0 bg-gradient-to-b from-transparent from-35% to-black/[0.08] opacity-0 transition-opacity duration-[360ms] ease-[cubic-bezier(0.22,0.55,0.28,1)] group-hover:opacity-100 group-active:opacity-100" />
          <span className="relative flex flex-col gap-0.5">
            <span className="text-[16px] font-bold leading-5 text-white">
              Weight loss
            </span>
            <span className="text-[13px] font-semibold leading-4 text-white/85 transition-colors duration-[360ms] ease-[cubic-bezier(0.22,0.55,0.28,1)] group-hover:text-white group-active:text-white">
              Start the quiz →
            </span>
          </span>
        </Link>
      </div>

      <div className="mt-3.5 flex flex-col">
        <RootMenuButton onClick={() => openPanel('services')}>
          Services
        </RootMenuButton>
        <RootMenuButton onClick={() => openPanel('treatments')}>
          Treatments
        </RootMenuButton>
        <RootMenuLink href="/prices" onClick={closeMenu}>
          Pricing
        </RootMenuLink>
        <RootMenuButton onClick={() => openPanel('about')}>
          About
        </RootMenuButton>
        {session && (
          <>
            <RootMenuButton onClick={() => openPanel('account')}>
              Account
            </RootMenuButton>
            <Link
              href="/portal"
              onClick={closeMenu}
              className="mt-2 flex min-h-[43px] items-center px-1 py-3 text-[15px] font-medium leading-[19px] text-[#82828b] transition-colors hover:text-[#0b0b0f] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[#2563eb]"
            >
              {isPatient ? 'Patient portal' : 'Portal'}
            </Link>
            <MobileLogoutButton
              title="Sign out"
              closeMenu={closeMenu}
              onLogout={onLogout}
            />
          </>
        )}
      </div>

      <div className="mt-auto flex flex-col gap-2.5 pt-4">
        {!session && (
          <Link
            href="/login"
            onClick={closeMenu}
            className="flex h-[46px] items-center justify-center rounded-full bg-[#f4f4f6] px-5 text-[15px] font-semibold leading-[18px] text-[#0b0b0f]"
          >
            Sign in
          </Link>
        )}
        <Link
          href="/start"
          onClick={closeMenu}
          className="flex h-[46px] items-center justify-center rounded-full bg-[#0b0b0f] px-5 text-[15px] font-semibold leading-[18px] text-white"
        >
          Request care
        </Link>
      </div>
    </div>
  )
}

function ServicesPanel({
  closeMenu,
  returnToMenu,
}: {
  closeMenu: () => void
  returnToMenu: () => void
}) {
  return (
    <PanelShell
      title="Services"
      closeMenu={closeMenu}
      returnToMenu={returnToMenu}
    >
      <div className="flex flex-col">
        {serviceNavigationItems.map((service) => (
          <Link
            key={service.href}
            href={service.href}
            onClick={closeMenu}
            className="flex min-h-[61px] items-center gap-3 border-b border-[#ececef] px-1 py-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[#2563eb]"
          >
            <ServiceIcon
              kind={service.icon}
              className="h-[34px] w-[34px]"
              iconClassName="h-3.5 w-3.5"
            />
            <span className="min-w-0 flex-1 text-[16px] font-semibold leading-5 text-[#0b0b0f]">
              {service.name}
            </span>
            <span className="shrink-0 text-[13px] leading-4 text-[#82828b]">
              {service.pricePrefix && `${service.pricePrefix} `}
              <PricingValue name={service.priceName} />
            </span>
          </Link>
        ))}
        <PanelFooterLink href="/start" closeMenu={closeMenu}>
          See all services →
        </PanelFooterLink>
      </div>
    </PanelShell>
  )
}

function TreatmentsPanel({
  closeMenu,
  returnToMenu,
}: {
  closeMenu: () => void
  returnToMenu: () => void
}) {
  return (
    <PanelShell
      title="Treatments"
      closeMenu={closeMenu}
      returnToMenu={returnToMenu}
    >
      <div className="flex flex-col">
        {treatmentNavigationItems.map((treatment) => (
          <Link
            key={treatment.href}
            href={treatment.href}
            onClick={closeMenu}
            className="flex min-h-[71px] items-center gap-3 border-b border-[#ececef] px-1 py-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[#2563eb]"
          >
            <Image
              src={treatment.image}
              alt=""
              className="h-11 w-11 shrink-0 rounded-[12px] object-cover object-[50%_30%]"
            />
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="text-[16px] font-semibold leading-5 text-[#0b0b0f]">
                {treatment.name}
              </span>
              <span className="text-[13px] leading-4 text-[#82828b]">
                {treatment.description}
              </span>
            </span>
          </Link>
        ))}
        <PanelFooterLink href="/start" closeMenu={closeMenu}>
          See all plans →
        </PanelFooterLink>
      </div>
    </PanelShell>
  )
}

function AboutPanel({
  closeMenu,
  returnToMenu,
}: {
  closeMenu: () => void
  returnToMenu: () => void
}) {
  const companyItems = aboutNavigationItems.filter(
    (item) => item.section === 'company',
  )
  const helpItems = aboutNavigationItems.filter(
    (item) => item.section === 'help',
  )
  const resultsItem = aboutNavigationItems.find(
    (item) => item.section === 'featured',
  )

  return (
    <PanelShell title="About" closeMenu={closeMenu} returnToMenu={returnToMenu}>
      <div className="flex flex-col">
        <MobileAboutSection
          title="Company"
          color="blue"
          items={companyItems}
          closeMenu={closeMenu}
        />
        <MobileAboutSection
          title="Help"
          color="teal"
          items={helpItems}
          closeMenu={closeMenu}
          separated
        />
        {resultsItem && (
          <MobileResultsLink item={resultsItem} closeMenu={closeMenu} />
        )}
      </div>
    </PanelShell>
  )
}

type AboutNavigationItem = (typeof aboutNavigationItems)[number]

function MobileAboutSection({
  title,
  color,
  items,
  closeMenu,
  separated = false,
}: {
  title: string
  color: 'blue' | 'teal'
  items: AboutNavigationItem[]
  closeMenu: () => void
  separated?: boolean
}) {
  return (
    <>
      <div
        className={`flex items-center gap-2 px-1 pb-2 ${separated ? 'pt-5' : 'pt-3.5'}`}
      >
        <span
          className={`h-1.5 w-1.5 shrink-0 rounded-full ${color === 'blue' ? 'bg-[#2563eb]' : 'bg-[#43bba9]'}`}
        />
        <span
          className={`text-[11px] font-semibold uppercase leading-[14px] tracking-[0.05em] ${color === 'blue' ? 'text-[#2563eb]' : 'text-[#1f8a70]'}`}
        >
          {title}
        </span>
      </div>
      {items.map((item, index) => (
        <Link
          key={item.href}
          href={item.href}
          onClick={closeMenu}
          className={`flex flex-col gap-0.5 px-1 py-[11px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[#2563eb] ${index < items.length - 1 ? 'border-b border-[#ececef]' : ''}`}
        >
          <span className="text-[16px] font-semibold leading-5 text-[#0b0b0f]">
            {item.name}
          </span>
          <span className="text-[12.5px] leading-4 text-[#82828b]">
            {item.description}
          </span>
        </Link>
      ))}
    </>
  )
}

function MobileResultsLink({
  item,
  closeMenu,
}: {
  item: AboutNavigationItem
  closeMenu: () => void
}) {
  return (
    <Link
      href={item.href}
      onClick={closeMenu}
      className="mt-5 flex items-center gap-3 rounded-[14px] bg-[#dff2ec] p-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[#1f8a70]"
    >
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white">
        <svg
          aria-hidden="true"
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            d="M3 12h4l2.5-6 4 12 2.5-6h5"
            stroke="#1f8a70"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
      <span className="flex min-w-0 flex-col gap-px">
        <span className="text-[15px] font-semibold leading-[19px] text-[#0b0b0f]">
          {item.name}
        </span>
        <span className="text-[12.5px] leading-4 text-[#3d5a4c]">
          How your results reach you
        </span>
      </span>
    </Link>
  )
}

function AccountPanel({
  links,
  closeMenu,
  returnToMenu,
}: {
  links: Array<{ title: string; href: string }>
  closeMenu: () => void
  returnToMenu: () => void
}) {
  return (
    <PanelShell
      title="Account"
      closeMenu={closeMenu}
      returnToMenu={returnToMenu}
    >
      <div className="flex flex-col">
        {links.map((item) => (
          <Link
            key={`${item.title}-${item.href}`}
            href={item.href}
            onClick={closeMenu}
            className="flex min-h-[53px] items-center border-b border-[#ececef] px-1 py-4 text-[16px] font-semibold leading-5 text-[#0b0b0f] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[#2563eb]"
          >
            {item.title}
          </Link>
        ))}
      </div>
    </PanelShell>
  )
}

function MobileMenuHeader({ closeMenu }: { closeMenu: () => void }) {
  return (
    <div className="flex items-center justify-between pb-[18px]">
      <Link
        href="/"
        onClick={closeMenu}
        aria-label="NextClinic home"
        className="rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#2563eb]"
      >
        <ResponsiveLogo />
      </Link>
      <CloseMenuButton closeMenu={closeMenu} />
    </div>
  )
}

function PanelShell({
  title,
  closeMenu,
  returnToMenu,
  children,
}: {
  title: string
  closeMenu: () => void
  returnToMenu: () => void
  children: ReactNode
}) {
  return (
    <div className="flex min-h-full flex-col px-5 pb-5 pt-3.5">
      <div className="flex items-center justify-between">
        <button
          type="button"
          aria-label={`Back from ${title}`}
          onClick={returnToMenu}
          className="focus-visible:outline-offset-3 -ml-2 flex h-10 w-10 items-center justify-center rounded-sm text-[#0b0b0f] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#2563eb]"
        >
          <FontAwesomeIcon
            icon={faChevronLeft}
            aria-hidden="true"
            className="!h-2.5 !w-2.5"
          />
        </button>
        <CloseMenuButton closeMenu={closeMenu} />
      </div>
      <h2 className="pb-5 pt-6 font-jakarta text-[28px] font-bold leading-[34px] tracking-[-0.02em] text-[#0b0b0f]">
        {title}
      </h2>
      {children}
    </div>
  )
}

function CloseMenuButton({ closeMenu }: { closeMenu: () => void }) {
  return (
    <button
      type="button"
      aria-label="Close navigation"
      onClick={closeMenu}
      className="focus-visible:outline-offset-3 -mr-2 flex h-10 w-10 items-center justify-center rounded-sm text-[#0b0b0f] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#2563eb]"
    >
      <svg
        aria-hidden="true"
        className="h-3.5 w-3.5"
        viewBox="0 0 16 16"
        fill="none"
      >
        <path
          d="M3 3 13 13M13 3 3 13"
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinecap="round"
        />
      </svg>
    </button>
  )
}

function RootMenuButton({
  children,
  onClick,
}: {
  children: ReactNode
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex min-h-[57px] items-center justify-between border-b border-[#ececef] px-1 py-[17px] text-left text-[17px] font-bold leading-[22px] text-[#0b0b0f] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[#2563eb]"
    >
      {children}
      <FontAwesomeIcon
        icon={faChevronRight}
        aria-hidden="true"
        className="h-4 w-4 text-[#82828b]"
      />
    </button>
  )
}

function RootMenuLink({
  children,
  href,
  onClick,
}: {
  children: ReactNode
  href: string
  onClick: () => void
}) {
  return (
    <Link
      href={href}
      onClick={onClick}
      className="flex min-h-[57px] items-center border-b border-[#ececef] px-1 py-[17px] text-[17px] font-bold leading-[22px] text-[#0b0b0f] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[#2563eb]"
    >
      {children}
    </Link>
  )
}

function PanelFooterLink({
  children,
  href,
  closeMenu,
}: {
  children: ReactNode
  href: string
  closeMenu: () => void
}) {
  return (
    <Link
      href={href}
      onClick={closeMenu}
      className="flex min-h-[50px] items-center px-1 py-4 text-[14px] font-semibold leading-[18px] text-[#2563eb] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[#2563eb]"
    >
      {children}
    </Link>
  )
}
